import React, { useState, useEffect } from 'react';
import { Box, IconButton, Chip, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import FormInputText from './FormInputText';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useTheme } from '@mui/material/styles';
import { Controller } from "react-hook-form";
import { generateGuid } from 'helpers';


export default function FormChipList({
  name,
  id,
  label,
  position = 'vertical',
  placeholder = '',
  disabled = false,
  helperText,
  onChange,
  defaultValue=[],
  control,
  showActions = true
}) {
  const methods = useForm();
  const { handleSubmit, setError, clearErrors, resetField } = methods;
  const controlInput = methods.control;
  const defaultId = !id ?? ('chip-list-' + generateGuid());
  const nameInput = `${name}-input`;
  const idInput = `${defaultId}-input`
  const [chipList, setChipList] = useState(defaultValue);

  const [t] = useTranslation();
  const theme = useTheme();

  useEffect(() => {
    resetField(nameInput);
    clearErrors(nameInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chipList]);

  const onSubmit = (data) => {
    if (!data[nameInput]) return;
    if (chipList.includes(data[nameInput])) {
      setError(nameInput, { message: t('common.components.chipList.error') });
      return;
    }
    const newChipList = [...chipList, data[nameInput]];
    setChipList(newChipList);
    onChange(newChipList);
  };

  const handleOnKeyDown = (e) => {
    if (e.keyCode === 13) onSubmit({ [nameInput]: e.target.value });
  };

  const handleDelete = (chipToDelete) => {
    if (!showActions) return;
    const newChipList = chipList.filter((c) => c !== chipToDelete);
    setChipList(newChipList);
    onChange(newChipList);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 2,
        width: '100%',
      }}
    >
      { showActions ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            position: 'relative',
            width: '100%',
          }}
        >
          <FormInputText
            name={nameInput}
            id={idInput}
            label={label}
            position={position}
            placeholder={placeholder}
            disabled={disabled}
            helperText={helperText}
            control={controlInput}
            onKeyDown={handleOnKeyDown}
          />
          <IconButton
            onClick={handleSubmit(onSubmit)}
            size="medium"
            sx={{
              position: 'absolute',
              top: 28,
              right: 0,
              zIndex: 2,
            }}
          >
            <AddIcon size="large" color="primary" />
          </IconButton>
        </Box>
      ): (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            position: 'relative',
            width: '100%',
          }}
        >
          <Typography variant="body1" id={`label-title-${label}`}>
            {t(label)}
          </Typography>
        </Box>
      )}
        <Controller
          control={control}
          name={name}
          id={defaultId}
          render={({ field: { onChange, value } }) => (
            <>
              { value.length > 0 && (
                <Stack
                  direction="row"
                  spacing={0}
                  sx={{
                    flexWrap: 'wrap',
                    border: `1px solid ${theme.palette.grey[400]}`,
                    borderRadius: 1,
                    width: '100%',
                    gap: 1,
                    p: 1,
                  }}
                >
                  { value.map((chip, index) => (
                    <Chip
                      key={index}
                      size="small"
                      label={chip}
                      onDelete={() => handleDelete(chip)}
                      deleteIcon={<CloseIcon />}
                      color="primary"
                    />
                  ))}
                </Stack>
              )}
            </>
          )}
        />
    </Box>
  );
}
